import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, finalize, take, filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { State } from '../../../../../store';
import { areasSelector, collectionsSelector } from '../../../../store/global.selectors';
import { GetOrganisationAreasAction, GetCollectionsAction } from '../../../../store/global.actions';
import { GetAdminOrganisationAreas } from '../../../../global.model';
import { Collection } from '../../../../services/collections/collections.model';
import {
    ReminderAnalyticsPeriod,
    ReminderAnalyticsQuery,
    ReminderSnapshotResponse,
    ReminderDistributionResponse,
    ReminderTrendsResponse,
} from '../../../../services/reminder-analytics/reminder-analytics.model';
import { ReminderAnalyticsService } from '../../../../services/reminder-analytics/reminder-analytics.abstract.service';

@Component({
    selector: 'app-collects-reminder-analytics',
    templateUrl: './collects-reminder-analytics.component.html',
    styleUrls: ['./collects-reminder-analytics.component.scss'],
})
export class CollectsReminderAnalyticsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    selectedPeriod: ReminderAnalyticsPeriod = '30d';
    comparePrevious = false;
    customFrom: string | null = null;
    customTo: string | null = null;
    showCustomDates = false;

    selectedChannels: string[] = [];
    selectedAreaId: string | null = null;
    areas: GetAdminOrganisationAreas[] = [];
    collections: Collection[] = [];

    loading = false;
    error = false;

    snapshot: ReminderSnapshotResponse | null = null;
    distribution: ReminderDistributionResponse | null = null;
    trends: ReminderTrendsResponse | null = null;

    periods: { value: ReminderAnalyticsPeriod; labelKey: string }[] = [
        { value: '7d', labelKey: 'pages.collections.analytics.period_7d' },
        { value: '30d', labelKey: 'pages.collections.analytics.period_30d' },
        { value: '90d', labelKey: 'pages.collections.analytics.period_90d' },
        { value: 'custom', labelKey: 'pages.collections.analytics.period_custom' },
    ];

    constructor(
        private analyticsService: ReminderAnalyticsService,
        public translate: TranslateService,
        private store: Store<State>,
    ) {}

    ngOnInit(): void {
        this.loadAreas();
        this.loadCollections();
        this.loadData();
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.loadData();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadAreas(): void {
        this.store
            .select(areasSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((areas) => {
                this.areas = areas || [];
            });

        this.store
            .select(areasSelector)
            .pipe(
                take(1),
                filter((areas) => areas.length === 0),
            )
            .subscribe(() => this.store.dispatch(GetOrganisationAreasAction()));
    }

    private loadCollections(): void {
        this.store
            .select(collectionsSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((collections) => {
                this.collections = collections || [];
            });

        this.store
            .select(collectionsSelector)
            .pipe(
                take(1),
                filter((collections) => collections.length === 0),
            )
            .subscribe(() => this.store.dispatch(GetCollectionsAction()));
    }

    onPeriodChange(period: ReminderAnalyticsPeriod): void {
        this.selectedPeriod = period;
        this.showCustomDates = period === 'custom';
        if (period !== 'custom') {
            this.customFrom = null;
            this.customTo = null;
            this.loadData();
        }
    }

    onCompareToggle(): void {
        this.comparePrevious = !this.comparePrevious;
        this.loadData();
    }

    onChannelChange(channels: string[]): void {
        this.selectedChannels = channels;
        this.loadData();
    }

    onAreaChange(areaId: string): void {
        this.selectedAreaId = areaId || null;
        this.loadData();
    }

    onCustomDateChange(): void {
        if (this.customFrom && this.customTo) {
            this.loadData();
        }
    }

    retry(): void {
        this.loadData();
    }

    loadData(): void {
        if (this.selectedPeriod === 'custom' && (!this.customFrom || !this.customTo)) {
            return;
        }

        const query: ReminderAnalyticsQuery = {
            period: this.selectedPeriod,
            comparePrevious: this.comparePrevious,
        };

        if (this.selectedPeriod === 'custom') {
            query.from = this.customFrom;
            query.to = this.customTo;
        }

        if (this.selectedChannels.length > 0) {
            query.channels = this.selectedChannels.join(',');
        }

        if (this.selectedAreaId) {
            query.areaIds = this.selectedAreaId;
        }

        this.loading = true;
        this.error = false;

        forkJoin({
            snapshot: this.analyticsService.getSnapshot(query),
            distribution: this.analyticsService.getDistribution(query),
            trends: this.analyticsService.getTrends(query),
        })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe({
                next: (result) => {
                    this.snapshot = result.snapshot;
                    this.distribution = result.distribution;
                    this.trends = result.trends;
                },
                error: () => {
                    this.error = true;
                    this.snapshot = null;
                    this.distribution = null;
                    this.trends = null;
                },
            });
    }
}
